// Menu Theme - Inspirado em Dragon Ball e Chrono Trigger
// Melodia épica e memorável com harmonias ricas

interface MenuThemeState {
  audioContext: AudioContext | null;
  masterGain: GainNode | null;
  compressor: DynamicsCompressorNode | null;
  oscillators: OscillatorNode[];
  gains: GainNode[];
  isPlaying: boolean;
  intervalId: ReturnType<typeof setInterval> | null;
  currentStep: number;
}

let menuThemeState: MenuThemeState = {
  audioContext: null,
  masterGain: null,
  compressor: null,
  oscillators: [],
  gains: [],
  isPlaying: false,
  intervalId: null,
  currentStep: 0,
};

// Flag para indicar se o usuário já interagiu
let userHasInteracted = false;
let pendingPlay: (() => void) | null = null;

// AudioContext compartilhado para garantir que resume funcione
let sharedAudioContext: AudioContext | null = null;

// Chamar quando o usuário interagir pela primeira vez
export function notifyUserInteraction(): void {
  if (userHasInteracted) return;
  userHasInteracted = true;
  console.log("[MenuTheme] User interaction detected");
  
  // Criar e resumir o AudioContext imediatamente na interação
  if (!sharedAudioContext || sharedAudioContext.state === "closed") {
    sharedAudioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  
  if (sharedAudioContext.state === "suspended") {
    sharedAudioContext.resume().then(() => {
      console.log("[MenuTheme] Shared AudioContext resumed on interaction");
      // Se há uma música pendente, tentar tocar
      if (pendingPlay) {
        const playFn = pendingPlay;
        pendingPlay = null;
        setTimeout(() => playFn(), 50);
      }
    }).catch((e) => {
      console.warn("[MenuTheme] Failed to resume on interaction:", e);
    });
  } else if (pendingPlay) {
    // Contexto já está running, executar pendingPlay
    const playFn = pendingPlay;
    pendingPlay = null;
    setTimeout(() => playFn(), 50);
  }
}

// Obter o AudioContext compartilhado (já resumido)
export function getSharedMenuAudioContext(): AudioContext | null {
  return sharedAudioContext;
}

// Force stop any existing menu music - call this before starting other music
export function forceStopMenuMusic(): void {
  if (menuThemeState.isPlaying) {
    menuThemeState.isPlaying = false;

    if (menuThemeState.intervalId) {
      clearInterval(menuThemeState.intervalId);
      menuThemeState.intervalId = null;
    }

    menuThemeState.oscillators.forEach((osc) => {
      try {
        osc.stop();
        osc.disconnect();
      } catch (e) {}
    });
    menuThemeState.oscillators = [];

    menuThemeState.gains.forEach((gain) => {
      try {
        gain.disconnect();
      } catch (e) {}
    });
    menuThemeState.gains = [];

    if (menuThemeState.masterGain) {
      try {
        menuThemeState.masterGain.disconnect();
      } catch (e) {}
      menuThemeState.masterGain = null;
    }

    if (menuThemeState.compressor) {
      try {
        menuThemeState.compressor.disconnect();
      } catch (e) {}
      menuThemeState.compressor = null;
    }

    console.log("[MenuTheme] Force stopped");
  }
}

// Notas musicais em Hz
const NOTE = {
  C3: 130.81, D3: 146.83, E3: 164.81, F3: 174.61, G3: 196.00, A3: 220.00, B3: 246.94,
  C4: 261.63, D4: 293.66, E4: 329.63, F4: 349.23, G4: 392.00, A4: 440.00, B4: 493.88,
  C5: 523.25, D5: 587.33, E5: 659.25, F5: 698.46, G5: 783.99, A5: 880.00, B5: 987.77,
  C6: 1046.50,
};

export function createMenuTheme(): {
  play: () => void;
  stop: () => void;
  volume: (v: number) => void;
  fade: (from: number, to: number, duration: number) => void;
  playing: () => boolean;
  state: () => string;
} | null {
  if (typeof window === "undefined") return null;

  const bpm = 125;
  const stepDuration = (60 / bpm) / 4; // 16th notes

  // Progressão de acordes estilo JRPG: C - G - Am - F (I - V - vi - IV)
  // 4 compassos, 16 steps cada = 64 steps total
  const chordProgression = [
    { root: NOTE.C3, third: NOTE.E3, fifth: NOTE.G3 },  // C major
    { root: NOTE.G3, third: NOTE.B3, fifth: NOTE.D4 },  // G major
    { root: NOTE.A3, third: NOTE.C4, fifth: NOTE.E4 },  // A minor
    { root: NOTE.F3, third: NOTE.A3, fifth: NOTE.C4 },  // F major
  ];

  const getChord = (step: number) => {
    const bar = Math.floor(step / 16) % 4;
    return chordProgression[bar];
  };

  // Melodia épica de 32 steps (2 compassos), inspirada em temas heroicos
  // Usa notas da escala de C maior com saltos melódicos expressivos
  const melodyNotes = [
    // Frase 1: Ascendente heroica
    NOTE.E4, 0, NOTE.G4, 0, NOTE.C5, 0, NOTE.D5, 0,
    NOTE.E5, 0, 0, NOTE.D5, NOTE.C5, 0, NOTE.G4, 0,
    // Frase 2: Resposta descendente
    NOTE.A4, 0, NOTE.G4, 0, NOTE.E4, 0, NOTE.D4, 0,
    NOTE.C4, 0, 0, NOTE.D4, NOTE.E4, 0, NOTE.G4, 0,
    // Frase 3: Variação com salto
    NOTE.C5, 0, NOTE.B4, 0, NOTE.A4, 0, NOTE.G4, 0,
    NOTE.A4, 0, NOTE.B4, 0, NOTE.C5, 0, 0, 0,
    // Frase 4: Resolução épica
    NOTE.E5, 0, NOTE.D5, 0, NOTE.C5, 0, NOTE.B4, 0,
    NOTE.C5, 0, 0, 0, 0, 0, 0, 0,
  ];

  // Arpejo suave estilo Chrono Trigger
  const arpPattern = [1, 0, 0.7, 0, 1, 0, 0.7, 0, 1, 0, 0.7, 0, 1, 0, 0.8, 0];

  // Contra-melodia harmonica
  const harmonyNotes = [
    NOTE.C4, 0, NOTE.E4, 0, NOTE.G4, 0, NOTE.E4, 0,
    NOTE.B3, 0, NOTE.D4, 0, NOTE.G4, 0, NOTE.D4, 0,
    NOTE.C4, 0, NOTE.E4, 0, NOTE.A4, 0, NOTE.E4, 0,
    NOTE.A3, 0, NOTE.C4, 0, NOTE.F4, 0, NOTE.C4, 0,
  ];

  // Baixo melódico
  const bassPattern = [1, 0, 0, 0, 0.6, 0, 0, 0, 0.8, 0, 0, 0, 0.6, 0, 0, 0];

  // Ritmo suave - kick e snare leves
  const kickPattern = [1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0];
  const snarePattern = [0, 0, 0, 0, 0.7, 0, 0, 0, 0, 0, 0, 0, 0.7, 0, 0, 0];

  let targetVolume = 0.5;
  let currentVolumeValue = 0;

  const theme = {
    play: () => {
      if (menuThemeState.isPlaying) return;

      try {
        // Usar o AudioContext compartilhado se disponível (já foi resumido na interação)
        if (sharedAudioContext && sharedAudioContext.state === "running") {
          menuThemeState.audioContext = sharedAudioContext;
        } else if (!menuThemeState.audioContext || menuThemeState.audioContext.state === "closed") {
          menuThemeState.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        }

        const ctx = menuThemeState.audioContext;

        // Se contexto suspenso e usuário ainda não interagiu, agendar para depois
        if (ctx.state === "suspended") {
          if (!userHasInteracted) {
            console.log("[MenuTheme] Audio suspended, waiting for user interaction...");
            pendingPlay = theme.play;
            return;
          }
          // Usuário já interagiu, tentar resume
          ctx.resume().then(() => {
            console.log("[MenuTheme] AudioContext resumed successfully");
            if (!menuThemeState.isPlaying) {
              theme.play();
            }
          }).catch((e) => {
            console.warn("[MenuTheme] Failed to resume AudioContext:", e);
          });
          return;
        }
        
        console.log("[MenuTheme] Starting playback, context state:", ctx.state);

        // Compressor para som mais polido
        menuThemeState.compressor = ctx.createDynamicsCompressor();
        menuThemeState.compressor.threshold.value = -20;
        menuThemeState.compressor.knee.value = 20;
        menuThemeState.compressor.ratio.value = 4;
        menuThemeState.compressor.attack.value = 0.003;
        menuThemeState.compressor.release.value = 0.15;
        menuThemeState.compressor.connect(ctx.destination);

        // Master gain
        menuThemeState.masterGain = ctx.createGain();
        menuThemeState.masterGain.gain.value = currentVolumeValue;
        menuThemeState.masterGain.connect(menuThemeState.compressor);

        menuThemeState.isPlaying = true;
        menuThemeState.currentStep = 0;

        // Criar osciladores
        // 0: Melodia principal (triangle - suave e clara)
        const melodyOsc = ctx.createOscillator();
        const melodyGain = ctx.createGain();
        const melodyFilter = ctx.createBiquadFilter();
        melodyOsc.type = "triangle";
        melodyFilter.type = "lowpass";
        melodyFilter.frequency.value = 2000;
        melodyFilter.Q.value = 1;
        melodyGain.gain.value = 0;
        melodyOsc.connect(melodyFilter);
        melodyFilter.connect(melodyGain);
        melodyGain.connect(menuThemeState.masterGain);
        melodyOsc.start();
        menuThemeState.oscillators.push(melodyOsc);
        menuThemeState.gains.push(melodyGain);

        // 1: Pad harmônico (sine - quente)
        const padOsc = ctx.createOscillator();
        const padGain = ctx.createGain();
        padOsc.type = "sine";
        padGain.gain.value = 0;
        padOsc.connect(padGain);
        padGain.connect(menuThemeState.masterGain);
        padOsc.start();
        menuThemeState.oscillators.push(padOsc);
        menuThemeState.gains.push(padGain);

        // 2: Pad terceira (sine)
        const pad3Osc = ctx.createOscillator();
        const pad3Gain = ctx.createGain();
        pad3Osc.type = "sine";
        pad3Gain.gain.value = 0;
        pad3Osc.connect(pad3Gain);
        pad3Gain.connect(menuThemeState.masterGain);
        pad3Osc.start();
        menuThemeState.oscillators.push(pad3Osc);
        menuThemeState.gains.push(pad3Gain);

        // 3: Pad quinta (sine)
        const pad5Osc = ctx.createOscillator();
        const pad5Gain = ctx.createGain();
        pad5Osc.type = "sine";
        pad5Gain.gain.value = 0;
        pad5Osc.connect(pad5Gain);
        pad5Gain.connect(menuThemeState.masterGain);
        pad5Osc.start();
        menuThemeState.oscillators.push(pad5Osc);
        menuThemeState.gains.push(pad5Gain);

        // 4: Arpejo (triangle - cristalino)
        const arpOsc = ctx.createOscillator();
        const arpGain = ctx.createGain();
        const arpFilter = ctx.createBiquadFilter();
        arpOsc.type = "triangle";
        arpFilter.type = "lowpass";
        arpFilter.frequency.value = 3000;
        arpGain.gain.value = 0;
        arpOsc.connect(arpFilter);
        arpFilter.connect(arpGain);
        arpGain.connect(menuThemeState.masterGain);
        arpOsc.start();
        menuThemeState.oscillators.push(arpOsc);
        menuThemeState.gains.push(arpGain);

        // 5: Baixo (sine - profundo)
        const bassOsc = ctx.createOscillator();
        const bassGain = ctx.createGain();
        const bassFilter = ctx.createBiquadFilter();
        bassOsc.type = "sine";
        bassFilter.type = "lowpass";
        bassFilter.frequency.value = 300;
        bassGain.gain.value = 0;
        bassOsc.connect(bassFilter);
        bassFilter.connect(bassGain);
        bassGain.connect(menuThemeState.masterGain);
        bassOsc.start();
        menuThemeState.oscillators.push(bassOsc);
        menuThemeState.gains.push(bassGain);

        // 6: Contra-melodia (sine - suave)
        const harmonyOsc = ctx.createOscillator();
        const harmonyGain = ctx.createGain();
        harmonyOsc.type = "sine";
        harmonyGain.gain.value = 0;
        harmonyOsc.connect(harmonyGain);
        harmonyGain.connect(menuThemeState.masterGain);
        harmonyOsc.start();
        menuThemeState.oscillators.push(harmonyOsc);
        menuThemeState.gains.push(harmonyGain);

        // 7: Kick (sine)
        const kickOsc = ctx.createOscillator();
        const kickGain = ctx.createGain();
        kickOsc.type = "sine";
        kickOsc.frequency.value = 60;
        kickGain.gain.value = 0;
        kickOsc.connect(kickGain);
        kickGain.connect(menuThemeState.masterGain);
        kickOsc.start();
        menuThemeState.oscillators.push(kickOsc);
        menuThemeState.gains.push(kickGain);

        menuThemeState.intervalId = setInterval(() => {
          if (!menuThemeState.isPlaying || !menuThemeState.audioContext) return;

          const step = menuThemeState.currentStep;
          const ctx = menuThemeState.audioContext;
          const now = ctx.currentTime;
          const chord = getChord(step);

          const [melodyOsc, padOsc, pad3Osc, pad5Osc, arpOsc, bassOsc, harmonyOsc, kickOsc] = menuThemeState.oscillators;
          const [melodyGain, padGain, pad3Gain, pad5Gain, arpGain, bassGain, harmonyGain, kickGain] = menuThemeState.gains;

          // Melodia principal
          const melodyNote = melodyNotes[step % melodyNotes.length];
          if (melodyNote > 0 && melodyOsc && melodyGain) {
            melodyOsc.frequency.setValueAtTime(melodyNote, now);
            melodyGain.gain.setValueAtTime(0.22, now);
            melodyGain.gain.exponentialRampToValueAtTime(0.08, now + stepDuration * 1.5);
          } else if (melodyGain) {
            melodyGain.gain.setTargetAtTime(0.001, now, 0.05);
          }

          // Pad harmônico sustentado
          if (padOsc && padGain && pad3Osc && pad3Gain && pad5Osc && pad5Gain) {
            padOsc.frequency.setTargetAtTime(chord.root, now, 0.1);
            pad3Osc.frequency.setTargetAtTime(chord.third, now, 0.1);
            pad5Osc.frequency.setTargetAtTime(chord.fifth, now, 0.1);

            const padVol = 0.08;
            padGain.gain.setTargetAtTime(padVol, now, 0.2);
            pad3Gain.gain.setTargetAtTime(padVol * 0.7, now, 0.2);
            pad5Gain.gain.setTargetAtTime(padVol * 0.6, now, 0.2);
          }

          // Arpejo
          const arpValue = arpPattern[step % arpPattern.length];
          if (arpValue > 0 && arpOsc && arpGain) {
            // Cicla entre root, third, fifth do acorde
            const arpNoteIndex = (step % 4);
            let arpFreq = chord.root * 2;
            if (arpNoteIndex === 1) arpFreq = chord.third * 2;
            else if (arpNoteIndex === 2) arpFreq = chord.fifth * 2;
            else if (arpNoteIndex === 3) arpFreq = chord.third * 2;

            arpOsc.frequency.setValueAtTime(arpFreq, now);
            arpGain.gain.setValueAtTime(0.06 * arpValue, now);
            arpGain.gain.exponentialRampToValueAtTime(0.001, now + stepDuration * 0.7);
          }

          // Baixo
          const bassValue = bassPattern[step % bassPattern.length];
          if (bassValue > 0 && bassOsc && bassGain) {
            bassOsc.frequency.setValueAtTime(chord.root * 0.5, now);
            bassGain.gain.setValueAtTime(0.18 * bassValue, now);
            bassGain.gain.exponentialRampToValueAtTime(0.001, now + stepDuration * 2);
          }

          // Contra-melodia (toca a cada 2 steps)
          const harmonyNote = harmonyNotes[Math.floor(step / 2) % harmonyNotes.length];
          if (step % 2 === 0 && harmonyNote > 0 && harmonyOsc && harmonyGain) {
            harmonyOsc.frequency.setValueAtTime(harmonyNote, now);
            harmonyGain.gain.setValueAtTime(0.04, now);
            harmonyGain.gain.exponentialRampToValueAtTime(0.001, now + stepDuration * 3);
          }

          // Kick suave
          const kickValue = kickPattern[step % kickPattern.length];
          if (kickValue > 0 && kickOsc && kickGain) {
            kickOsc.frequency.setValueAtTime(80, now);
            kickOsc.frequency.exponentialRampToValueAtTime(40, now + 0.1);
            kickGain.gain.setValueAtTime(0.12 * kickValue, now);
            kickGain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
          }

          // Snare suave com noise (criado dinamicamente)
          const snareValue = snarePattern[step % snarePattern.length];
          if (snareValue > 0) {
            const snareNoise = ctx.createOscillator();
            const snareGain = ctx.createGain();
            const snareFilter = ctx.createBiquadFilter();
            snareNoise.type = "triangle";
            snareNoise.frequency.value = 200;
            snareFilter.type = "highpass";
            snareFilter.frequency.value = 1000;
            snareGain.gain.setValueAtTime(0.05 * snareValue, now);
            snareGain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
            snareNoise.connect(snareFilter);
            snareFilter.connect(snareGain);
            snareGain.connect(menuThemeState.masterGain!);
            snareNoise.start(now);
            snareNoise.stop(now + 0.1);
          }

          menuThemeState.currentStep++;
        }, stepDuration * 1000);

        console.log("[MenuTheme] Started playing - JRPG style");
      } catch (error) {
        console.error("[MenuTheme] Error starting:", error);
      }
    },

    stop: () => {
      if (!menuThemeState.isPlaying) return;

      menuThemeState.isPlaying = false;

      if (menuThemeState.intervalId) {
        clearInterval(menuThemeState.intervalId);
        menuThemeState.intervalId = null;
      }

      menuThemeState.oscillators.forEach((osc) => {
        try {
          osc.stop();
          osc.disconnect();
        } catch (e) {}
      });
      menuThemeState.oscillators = [];

      menuThemeState.gains.forEach((gain) => {
        try {
          gain.disconnect();
        } catch (e) {}
      });
      menuThemeState.gains = [];

      if (menuThemeState.masterGain) {
        menuThemeState.masterGain.disconnect();
        menuThemeState.masterGain = null;
      }

      if (menuThemeState.compressor) {
        menuThemeState.compressor.disconnect();
        menuThemeState.compressor = null;
      }

      console.log("[MenuTheme] Stopped");
    },

    volume: (v: number) => {
      targetVolume = Math.max(0, Math.min(1, v));
      currentVolumeValue = targetVolume;
      if (menuThemeState.masterGain) {
        menuThemeState.masterGain.gain.value = targetVolume;
      }
    },

    fade: (from: number, to: number, duration: number) => {
      if (!menuThemeState.masterGain || !menuThemeState.audioContext) return;

      const now = menuThemeState.audioContext.currentTime;
      menuThemeState.masterGain.gain.setValueAtTime(from, now);
      menuThemeState.masterGain.gain.linearRampToValueAtTime(to, now + duration / 1000);
      currentVolumeValue = to;
      targetVolume = to;
    },

    playing: () => menuThemeState.isPlaying,

    state: () => menuThemeState.isPlaying ? "loaded" : "unloaded",
  };

  return theme;
}

export function fadeOutHowl(theme: ReturnType<typeof createMenuTheme>, duration: number = 1000) {
  if (!theme || !theme.playing()) return;

  // Force immediate stop to prevent audio bleeding
  forceStopMenuMusic();
  console.log("[fadeOutHowl] Menu music stopped immediately");
}

export function fadeInHowl(theme: ReturnType<typeof createMenuTheme>, targetVolume: number = 0.5, duration: number = 1000) {
  if (!theme) return;

  theme.volume(0);
  theme.play();

  const steps = 25;
  const stepTime = duration / steps;
  let currentStep = 0;

  const interval = setInterval(() => {
    currentStep++;
    const progress = currentStep / steps;
    // Ease in curve
    const eased = progress * progress;
    const newVolume = targetVolume * eased;
    theme.volume(newVolume);

    if (currentStep >= steps) {
      clearInterval(interval);
      console.log("[fadeInHowl] Fade in complete");
    }
  }, stepTime);
}
